/**
 * 관리자용 평가사 관리 페이지
 *
 * EvalAdminPage.js에서 "평가사 관리" 버튼으로 진입
 * 또는 Dashboard.js에 별도 라우트로 추가
 *
 * 기능:
 * - 평가사 목록 (등급별 정렬, 평점/건수 표시)
 * - 평가사 상세 프로필 (→ InspectorDetail.js)
 * - 평가사 등록/수정
 * - 등급 자동 계산 (마스터/시니어/일반)
 *
 * 2026-10-29 — 화면을 v2 키트(히어로·카드·장부)로 '업체 정산'(v2/eval/parts/UsageCompanies)과 맞춤.
 *   조회·승인·등록·삭제 로직은 그대로.
 */
import React, { useState, useEffect } from 'react';
import { API_ORIGIN, authFetch } from '../utils/api';
import { useIsMobile } from '../utils/useMobile';
import { T, S, FONT, NUM } from '../v2/theme';
import { HeroStrip, Card, Ledger, EmptyState, ViewToggle, Pill } from '../v2/kit';
import { LevelBadge } from './CommonUI';
import PageBar from '../v2/eval/parts/PageBar';
import InspectorDetail from './InspectorDetail';
import useDetailHistory from '../v2/eval/hooks/useDetailHistory';
import CustomModal from './CustomModal';

var sub = { fontSize: FONT.label, color: T.faint };
var linkSm = { fontSize: FONT.label, fontWeight: 700, color: T.accentPress, textDecoration: 'none' };
var SORTS = [{ id: 'rating', label: '평점순' }, { id: 'jobs', label: '검차 수' }, { id: 'recent', label: '최근 활동' }];

var BASE = API_ORIGIN;

// 시간 경과 표시
var getTimeAgo = function(dateStr) {
  if (!dateStr) return '정보 없음';
  var diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return '방금 전';
  if (diff < 3600) return Math.floor(diff / 60) + '분 전';
  if (diff < 86400) return Math.floor(diff / 3600) + '시간 전';
  return Math.floor(diff / 86400) + '일 전';
};

// ─── 평가사 등록 폼 ───
function InspectorForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ name: '', phone: '', area: '', pin_code: '0000' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var field = function(key, label, ph, extra) {
    return (<label style={{ display: 'block', minWidth: 0 }}>
      <span style={S.label}>{label}</span>
      <input value={form[key]} onChange={e => set(key, e.target.value)} placeholder={ph} style={Object.assign({}, S.input, { width: '100%', boxSizing: 'border-box' })} {...(extra || {})} />
    </label>);
  };

  return (
    <Card title="평가사 등록" style={{ marginBottom: 14 }} pad={16} right={<button type="button" style={Object.assign({}, S.btnGhost, S.btnSm)} onClick={onCancel}>닫기</button>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        {field('name', '이름 *', '홍길동')}
        {field('phone', '전화번호 *', '010-0000-0000')}
        {field('area', '활동 지역', '인천/경기서부')}
        {field('pin_code', 'PIN 코드', '4자리', { maxLength: 4 })}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={S.btn}>취소</button>
        <button type="button" onClick={() => { if (!form.name || !form.phone) { setModal({ type: 'danger', title: '실패', message: '이름과 전화번호는 필수', hideCancel: true, onConfirm: closeModal }); return; } onSubmit(form); }} style={S.btnPrimary}>등록</button>
      </div>
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </Card>
  );
}

// ─── 메인: 평가사 목록 ───
// API에서 상세 데이터 로딩
function InspectorDetailLoader({ id, fallback, onBack, onRefreshList }) {
  var [data, setData] = useState(null);
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var fetchDetail = function() {
    authFetch(BASE + '/api/eval/inspectors/' + id).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setData(d.data);
    }).catch(function() {});
  };
  useEffect(function() { fetchDetail(); }, [id]);
  var inspector = data || Object.assign({}, fallback, { reviews: [], avg_scores: { speed: 0, accuracy: 0, detail: 0 }, distribution: [] });
  var handleUpdate = function(inspId, form) {
    authFetch(BASE + '/api/eval/inspectors/' + inspId, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) { fetchDetail(); if (onRefreshList) onRefreshList(); }
      else setModal({ type: 'danger', title: '실패', message: d.error || '수정 실패', hideCancel: true, onConfirm: closeModal });
    }).catch(function() {});
  };
  return (<>
    <InspectorDetail inspector={inspector} onBack={onBack} onUpdate={handleUpdate} />
    {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
  </>);
}

export default function InspectorManage({ onBack }) {
  var isMobile = useIsMobile();
  var [inspectors, setInspectors] = useState([]);
  var [selectedId, setSelectedId] = useState(null);
  // 브라우저 뒤로가기 = 상세 → 목록 (2026-10-30)
  var backToList = useDetailHistory('inspector', !!selectedId, function() { setSelectedId(null); });
  var [showForm, setShowForm] = useState(false);
  var [sortBy, setSortBy] = useState('rating');
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };

  var fetchInspectors = function() {
    // include_inactive=1 — 셀프가입 승인대기(pending) + 비활성 평가사도 노출 (승인/관리 위해).
    authFetch(BASE + '/api/eval/inspectors?include_inactive=1').then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setInspectors(d.data || []);
    }).catch(function() {});
  };

  useEffect(function() { fetchInspectors(); }, []);

  // 셀프가입(pending) 승인 — PUT { status:'active' }. 승인 후 앱 로그인 가능.
  var approveInspector = function(ins) {
    setModal({
      type: 'confirm', title: '평가사 승인',
      message: ins.name + ' 평가사를 승인하시겠습니까?\n승인 후 앱에서 로그인할 수 있습니다.',
      confirmText: '승인',
      onConfirm: function() {
        closeModal();
        authFetch(BASE + '/api/eval/inspectors/' + ins.id, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'active' }),
        }).then(function(r) { return r.json(); }).then(function(d) {
          if (d.success) fetchInspectors();
          else setModal({ type: 'danger', title: '실패', message: d.error || '승인 실패', hideCancel: true, onConfirm: closeModal });
        }).catch(function() { setModal({ type: 'danger', title: '실패', message: '서버 연결 실패', hideCancel: true, onConfirm: closeModal }); });
      },
    });
  };

  // 정렬
  const sorted = [...inspectors].sort((a, b) => {
    if (sortBy === 'rating') return b.avg_rating - a.avg_rating;
    if (sortBy === 'jobs') return b.total_jobs - a.total_jobs;
    if (sortBy === 'recent') return b.recent_jobs - a.recent_jobs;
    return 0;
  });

  // 상세 페이지
  if (selectedId) {
    var inspector = inspectors.find(function(i) { return i.id === selectedId; });
    if (!inspector) { setSelectedId(null); return null; }
    return <InspectorDetailLoader id={selectedId} fallback={inspector} onBack={backToList} onRefreshList={fetchInspectors} />;
  }

  // 전체 통계
  const totalActive = inspectors.filter(i => i.status === 'active').length;
  const totalPending = inspectors.filter(i => i.status === 'pending').length;
  // mysql DECIMAL → string '+' concat 위험 대응 (Number 강제)
  const avgRating = inspectors.length ? +(inspectors.reduce((s, i) => s + Number(i.avg_rating || 0), 0) / inspectors.length).toFixed(1) : 0;
  const totalJobs = inspectors.reduce((s, i) => s + i.total_jobs, 0);

  var deleteInspector = function(ins) {
    setModal({ type: 'danger', title: '평가사 삭제', message: ins.name + ' 평가사를 삭제하시겠습니까?\n진행중인 오더가 있으면 삭제할 수 없습니다.', confirmText: '삭제', onConfirm: function() { closeModal(); authFetch(BASE + '/api/eval/inspectors/' + ins.id, { method: 'DELETE' }).then(function(r) { return r.json(); }).then(function(d) { if (d.success) fetchInspectors(); else setModal({ type: 'danger', title: '실패', message: d.error || '삭제 실패', hideCancel: true, onConfirm: closeModal }); }).catch(function() {}); } });
  };

  return (
    <div>
      {/* onBack — 탭이 없는 v1 EvalAdminPage 전용. v2 EvalScreen 은 탭으로 이동하므로 넘기지 않는다. */}
      <PageBar back={onBack ? { label: '‹ 평가 오더', onClick: onBack } : null} title={'평가사 관리 · ' + inspectors.length + '명'} right={<React.Fragment>
        <ViewToggle value={sortBy} onChange={setSortBy} options={SORTS}/>
        <button type="button" style={Object.assign({}, S.btnPrimary, S.btnSm)} onClick={() => setShowForm(!showForm)}>+ 평가사 등록</button>
      </React.Fragment>}/>
      <HeroStrip isMobile={isMobile} metrics={[
        { key: 'active', label: '활동 평가사', value: totalActive, unit: '명' },
        { key: 'pending', label: '승인 대기', value: totalPending, unit: '명', tone: totalPending ? 'danger' : 'faint', sub: totalPending ? '목록에서 승인' : undefined },
        { key: 'rating', label: '평균 평점', value: avgRating, unit: '★' },
        { key: 'jobs', label: '총 검차', value: totalJobs, unit: '건' },
      ]}/>

      {/* 등록 폼 */}
      {showForm && (
        <InspectorForm
          onSubmit={function(form) {
            authFetch(BASE + '/api/eval/inspectors', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(form),
            }).then(function(r) { return r.json(); }).then(function(d) {
              if (d.success) { fetchInspectors(); setShowForm(false); }
              else setModal({ type: 'danger', title: '실패', message: d.error || '등록 실패', hideCancel: true, onConfirm: closeModal });
            }).catch(function() { setModal({ type: 'danger', title: '실패', message: '서버 연결 실패', hideCancel: true, onConfirm: closeModal }); });
          }}
          onCancel={function() { setShowForm(false); }}
        />
      )}

      <Card>
        {!sorted.length ? <EmptyState loaded text="등록된 평가사가 없습니다"/> : (
          <Ledger minWidth={820} foot={<React.Fragment>
            <span>평가사 <b style={NUM}>{sorted.length}</b>명</span><span className="mute">행을 누르면 프로필 · 평점은 업체 리뷰 평균</span>
          </React.Fragment>}>
            <thead><tr>
              <th>평가사</th><th style={{ width: 130 }}>지역</th><th style={{ width: 130 }}>연락처</th>
              <th style={{ width: 90 }}>평점</th><th className="num" style={{ width: 80 }}>총 검차</th><th className="num" style={{ width: 90 }}>최근 30일</th>
              <th style={{ width: 150 }}>위치</th><th style={{ width: 60 }}/>
            </tr></thead>
            <tbody>
              {sorted.map(ins => {
                return (<tr key={ins.id} onClick={() => setSelectedId(ins.id)} style={{ cursor: 'pointer' }}>
                  <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span className="k">{ins.name}</span><LevelBadge level={ins.level || '일반'}/>
                    {ins.status === 'pending' ? <Pill tone="warn">승인대기</Pill> : ins.status !== 'active' ? <Pill tone="gray">비활성</Pill> : null}
                    {ins.status === 'pending' && (
                      <button type="button" title="승인" onClick={function(e) { e.stopPropagation(); approveInspector(ins); }}
                        style={Object.assign({}, S.btnNavy, S.btnSm)}>승인</button>
                    )}
                  </span></td>
                  <td>{ins.area || <span className="mute">-</span>}</td>
                  <td style={NUM}>{ins.phone}</td>
                  {/* 목록 API 는 항목 평점(avg_scores)을 주지 않는다 — 평균만. 리뷰가 없으면 '-' */}
                  <td>{Number(ins.avg_rating) > 0 ? <span><b style={NUM}>{ins.avg_rating}</b><span style={{ color: T.dotWarn }}> ★</span></span> : <span className="mute">-</span>}</td>
                  <td className="num" style={NUM}>{ins.total_jobs}</td>
                  <td className="num" style={NUM}>{ins.recent_jobs || 0}</td>
                  <td>{ins.current_lat && ins.current_lng ? (<span>
                    <a href={'https://map.naver.com/v5/?c=' + ins.current_lng + ',' + ins.current_lat + ',15,0,0,0,dh'} target="_blank" rel="noopener noreferrer"
                      onClick={function(e) { e.stopPropagation(); }} style={linkSm}>지도에서 보기</a>
                    <div style={sub}>{getTimeAgo(ins.location_updated_at)}</div>
                  </span>) : <span className="mute">-</span>}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button type="button" title="삭제" onClick={function(e) { e.stopPropagation(); deleteInspector(ins); }}
                      style={Object.assign({}, S.btnGhost, S.btnSm, { color: T.danger })}>삭제</button>
                  </td>
                </tr>);
              })}
            </tbody>
          </Ledger>
        )}
      </Card>
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}
